import desertBgm from '../../../assets/cat-running/bgm/desert-bgm.mp3'
import forestBgm from '../../../assets/cat-running/bgm/forest-bgm.mp3'
import iceBgm from '../../../assets/cat-running/bgm/ice-bgm.mp3'
import volcanoBgm from '../../../assets/cat-running/bgm/volcano-gbm.mp3'
import { CAT_RUNNING_STAGE_KEYS } from '../constants'

const STAGE_BGM_SRC = {
  forest: forestBgm,
  desert: desertBgm,
  ice: iceBgm,
  volcano: volcanoBgm,
}

const TARGET_VOLUME = 0.35
const FADE_IN_MS = 420
const CROSSFADE_MS = 700
const MUTE_STORAGE_KEY = 'cat-running-bgm-muted'
const DEFAULT_STAGE_KEY = CAT_RUNNING_STAGE_KEYS[0]

let muted = false
let audioUnlocked = false
/** 시작 버튼 직후: 재생은 하되 볼륨 0 유지 → 랜덤 스테이지 확정 후 처음 들리게 */
let holdSilent = false
let activeStageKey = null
let desiredStageKey = null
let playGeneration = 0
let fadeFrameId = null
const audioByStage = {}

const readStoredMuted = () => {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const writeStoredMuted = (value) => {
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, value ? '1' : '0')
  } catch {
    // Ignore storage failures in private mode.
  }
}

const resolveStageKey = (stageKey) =>
  CAT_RUNNING_STAGE_KEYS.includes(stageKey) ? stageKey : DEFAULT_STAGE_KEY

const getStageAudio = (stageKey) => {
  const resolvedKey = resolveStageKey(stageKey)
  if (!audioByStage[resolvedKey]) {
    const audio = new Audio(STAGE_BGM_SRC[resolvedKey])
    audio.loop = true
    audio.preload = 'auto'
    audio.volume = 0
    audioByStage[resolvedKey] = audio
  }
  return audioByStage[resolvedKey]
}

const clearFade = () => {
  if (fadeFrameId) {
    window.cancelAnimationFrame(fadeFrameId)
    fadeFrameId = null
  }
}

const applyVolume = (audio, volume) => {
  if (!audio) return
  audio.volume = muted || holdSilent ? 0 : volume
}

const pauseOtherTracks = (keepAudio) => {
  Object.values(audioByStage).forEach((audio) => {
    if (audio === keepAudio) return
    audio.pause()
  })
}

const stopAllTracks = () => {
  clearFade()
  Object.values(audioByStage).forEach((audio) => {
    audio.pause()
    try {
      audio.currentTime = 0
    } catch {
      // Ignore seek failures before metadata is ready.
    }
    audio.volume = 0
  })
}

const restoreActiveVolume = () => {
  if (holdSilent || muted) return
  const audio = activeStageKey ? audioByStage[activeStageKey] : null
  if (!audio || audio.paused) return
  audio.volume = TARGET_VOLUME
}

/**
 * 이전 트랙과 새 트랙을 겹쳐 볼륨을 교환한다.
 * 시작 무음 → 랜덤 스테이지: previous 볼륨 0이라 자연스러운 fade-in만 들린다.
 */
const crossfadeTo = (nextAudio, previousAudio, generation) => {
  clearFade()
  if (muted || holdSilent) {
    nextAudio.volume = 0
    if (previousAudio && previousAudio !== nextAudio) {
      previousAudio.pause()
      previousAudio.volume = 0
    }
    return
  }

  const startedAt = performance.now()
  const prevFrom = previousAudio && previousAudio !== nextAudio ? previousAudio.volume : 0
  const nextFrom = nextAudio.volume
  const duration = prevFrom > 0.02 ? CROSSFADE_MS : FADE_IN_MS

  const step = (now) => {
    if (generation !== playGeneration) {
      fadeFrameId = null
      return
    }

    const progress = Math.min(1, (now - startedAt) / duration)
    nextAudio.volume = nextFrom + (TARGET_VOLUME - nextFrom) * progress

    if (previousAudio && previousAudio !== nextAudio) {
      previousAudio.volume = prevFrom * (1 - progress)
    }

    if (progress < 1) {
      fadeFrameId = window.requestAnimationFrame(step)
      return
    }

    fadeFrameId = null
    applyVolume(nextAudio, TARGET_VOLUME)
    if (previousAudio && previousAudio !== nextAudio) {
      previousAudio.pause()
      previousAudio.volume = 0
      try {
        previousAudio.currentTime = 0
      } catch {
        // ignore
      }
    }
  }

  fadeFrameId = window.requestAnimationFrame(step)
}

const unlockOtherTracksSync = (keepStageKey) => {
  const keepKey = resolveStageKey(keepStageKey)

  CAT_RUNNING_STAGE_KEYS.forEach((stageKey) => {
    if (stageKey === keepKey) return

    const audio = getStageAudio(stageKey)
    audio.volume = 0
    const playPromise = audio.play()
    if (!playPromise || typeof playPromise.then !== 'function') return

    playPromise
      .then(() => {
        audioUnlocked = true
        audio.pause()
        try {
          audio.currentTime = 0
        } catch {
          // ignore
        }
      })
      .catch(() => {})
  })
}

const beginStagePlayback = (
  stageKey,
  { fromGesture = false, unlockOthers = false, silent = false, reveal = false } = {},
) => {
  const resolvedKey = resolveStageKey(stageKey)
  const nextAudio = getStageAudio(resolvedKey)
  desiredStageKey = resolvedKey

  if (reveal) {
    holdSilent = false
  }

  if (muted) {
    return Promise.resolve(false)
  }

  if (activeStageKey === resolvedKey && !nextAudio.paused) {
    audioUnlocked = true
    if (silent || holdSilent) {
      nextAudio.volume = 0
      return Promise.resolve(true)
    }
    // 같은 트랙 무음 → 가청
    const generation = ++playGeneration
    crossfadeTo(nextAudio, null, generation)
    return Promise.resolve(true)
  }

  const previousKey = activeStageKey
  const previousAudio = previousKey ? audioByStage[previousKey] : null
  const wasPaused = nextAudio.paused
  const resumingSameTrack = wasPaused && previousKey === resolvedKey

  if (wasPaused && !resumingSameTrack) {
    try {
      nextAudio.currentTime = 0
    } catch {
      // ignore
    }
  }

  nextAudio.volume = 0
  const playPromise = nextAudio.play()

  if (fromGesture && unlockOthers) {
    unlockOtherTracksSync(resolvedKey)
  }

  if (!playPromise || typeof playPromise.then !== 'function') {
    if (!holdSilent) restoreActiveVolume()
    return Promise.resolve(false)
  }

  return playPromise
    .then(() => {
      if (desiredStageKey !== resolvedKey) {
        if (nextAudio !== previousAudio) {
          nextAudio.pause()
        }
        if (!holdSilent) restoreActiveVolume()
        return false
      }

      const generation = ++playGeneration
      audioUnlocked = true
      activeStageKey = resolvedKey

      if (silent || holdSilent) {
        nextAudio.volume = 0
        // 무음 프라임 중에는 이전 트랙도 정리하되 가청 전환은 하지 않음
        if (previousAudio && previousAudio !== nextAudio) {
          previousAudio.pause()
          previousAudio.volume = 0
        }
        return true
      }

      // 성공 후에만 crossfade (실패 시 기존 트랙 유지)
      crossfadeTo(nextAudio, previousAudio, generation)
      return true
    })
    .catch(() => {
      if (!holdSilent) restoreActiveVolume()
      return false
    })
}

export const catRunningBgm = {
  preload() {
    CAT_RUNNING_STAGE_KEYS.forEach((stageKey) => {
      getStageAudio(stageKey)
    })
  },

  isMuted() {
    return muted
  },

  isUnlocked() {
    return audioUnlocked
  },

  isHoldingSilent() {
    return holdSilent
  },

  isPlaying() {
    const audio = activeStageKey ? audioByStage[activeStageKey] : null
    return Boolean(audio && !audio.paused)
  },

  getActiveStageKey() {
    return activeStageKey
  },

  ensureAudible() {
    if (muted || holdSilent) return false
    restoreActiveVolume()
    const audio = activeStageKey ? audioByStage[activeStageKey] : null
    return Boolean(audio && !audio.paused)
  },

  loadMutedPreference() {
    muted = readStoredMuted()
    return muted
  },

  setMuted(value) {
    muted = Boolean(value)
    writeStoredMuted(muted)

    if (muted) {
      const audio = activeStageKey ? audioByStage[activeStageKey] : null
      if (audio) audio.volume = 0
      return Promise.resolve(true)
    }

    holdSilent = false
    const stageKey = desiredStageKey ?? activeStageKey ?? DEFAULT_STAGE_KEY
    return this.playStage(stageKey)
  },

  toggleMuted() {
    this.setMuted(!muted)
    return muted
  },

  /**
   * 게임시작 버튼: 기본 트랙을 무음으로 재생해 unlock만 한다.
   * 랜덤 스테이지가 정해지기 전에 forest가 들리지 않게 한다.
   */
  primeFromStartButton() {
    holdSilent = true
    return beginStagePlayback(DEFAULT_STAGE_KEY, {
      fromGesture: true,
      unlockOthers: false,
      silent: true,
    })
  },

  unlockFromUserGesture(preferredStageKey) {
    const stageKey = preferredStageKey ?? desiredStageKey ?? activeStageKey ?? DEFAULT_STAGE_KEY
    return beginStagePlayback(stageKey, {
      fromGesture: true,
      unlockOthers: true,
      reveal: true,
    })
  },

  /** 게임 진입 후 실제 스테이지 BGM을 들리게 한다 (crossfade) */
  playStage(stageKey) {
    return beginStagePlayback(stageKey, {
      fromGesture: false,
      unlockOthers: false,
      reveal: true,
    })
  },

  pause() {
    playGeneration += 1
    clearFade()
    Object.values(audioByStage).forEach((audio) => audio.pause())
  },

  stop() {
    playGeneration += 1
    activeStageKey = null
    desiredStageKey = null
    audioUnlocked = false
    holdSilent = false
    stopAllTracks()
  },
}

export default catRunningBgm
